// libraries
import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import Barcode from 'react-barcode'
import { Big } from 'big.js'
import { Alert, Grid, Box, Button, IconButton, Typography, Stack, Divider, Tooltip } from '@mui/material'
import {
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
  Print as PrintIcon,
  Flag as FlagIcon,
  OpenInNew as OpenInNewIcon,
  Fingerprint as FingerprintIcon,
  CreditCard as CreditCardIcon,
  CameraAlt as CameraAltIcon,
  WarningAmberSharp as WarningAmberSharpIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { randomId } from '@mui/x-data-grid-generator'
import printJS from 'print-js'
import { v4 as uuidv4 } from 'uuid'
import { groupBy } from 'lodash'

// state
import { showFormModal } from 'state/ui'

// queries
import {
  useGetInboundTicketQuery,
  useUpdateInboundTicketMutation,
  useGetInboundTicketNotesQuery,
} from 'services/inboundTickets'
import { useGetBillQuery } from 'services/bills'
import { useGetCustomerQuery } from 'services/customers'
import {
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useVoidPaymentMutation,
  useGetInboundTicketQueuePaymentsQuery,
} from 'services/payments'
import { useGetOrgImageQuery } from 'services/assets'
import { useGetVehicleQuery } from 'services/vehicles'
import { useGetGsUserQuery } from 'services/gsUsers'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { useGetCommoditiesQuery } from 'services/commodities'
import { useGetPurchaseOrderQuery } from 'services/purchaseOrders'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import PaymentMethodRow from 'components/containers/inbound-tickets/components/PaymentMethodRow'
import ScaleReceiptForm from 'components/containers/inbound-tickets/components/ScaleReceiptForm'
import PrintCheckBlade from 'components/containers/bills/components/PrintCheckBlade'
import HighBalancePaymentAlert from 'components/containers/bills/components/HighBalancePaymentAlert'
import CommodityWeightThresholdAlert from 'components/containers/bills/components/CommodityWeightThresholdAlert'
import CommodityTypeCostThresholdAlert from '../components/CommodityTypeCostThresholdAlert'
import UseCostThresholdAlert from 'components/containers/bills/components/useCostThresholdAlert'

// generic components
import AlertSnackbar from 'components/generic/snackbars'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import Loading from 'components/generic/Loading'
import { Chip } from 'components/generic/chip'
import Input from 'components/generic/form/Input'
import CheckTemplate from 'components/generic/printable/CheckTemplate'
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintableBill from 'components/generic/printable/PrintableBill'
import CaptureFingerprintModal from 'components/generic/modals/dialog-modal/CaptureFingerprintModal'
import CaptureSignatureModal from 'components/generic/modals/dialog-modal/CaptureSignatureModal'
import CapturePhotoModal from 'components/generic/modals/dialog-modal/CapturePhotoModal'
import EmailModal from 'components/generic/modals/dialog-modal/EmailModal'

// helpers and utils
import {
  formatIbt,
  generatePaymentMethodText,
  generateCheckNumberText,
} from 'components/containers/inbound-tickets/utils'
import {
  iconMapper,
  renderActiveDialog,
  PRINT_ACTION,
} from 'components/containers/inbound-tickets/helpers/ticketActionsAndStatus'
import { formOptions } from 'utils/validation/inboundTicketPayment'
import CurrencyToWords from 'utils/data-manipulation/currencyToWords'
import { dateFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { formatScrapVehicles, getContactAddress } from 'utils/data-manipulation/common'
import {
  BILL_STATUSES,
  PAYMENT_METHODS_VALUES,
  TICKET_STATUSES,
  PAYMENT_STATUSES,
  PRINT_DIALOG_TYPES,
  CHURN_ZERO_EVENTS as cz,
  DEFAULT_SUPPLIER_TYPES,
  ROUNDING_PRECISIONS,
  GS_ERROR_CODE,
  ENTITY_IN_LOCK_PERIOD_MESSAGE,
  ACCOUNT_PAYMENT_TYPE_ENUMS,
  COMMODITY_RULE_TYPES,
  COMMODITY_WEIGHT_THRESHOLD_ALERT_MESSAGE,
  COMMODITY_TYPE_COST_THRESHOLD_ALERT_MESSAGE,
  PDF_TYPE,
} from 'utils/constants'
import FORM_MODALS from 'utils/ui/modals'
import {
  formatNumber,
  setDecimalPrecision,
  dispatchSnackbar,
  isDateInPast,
  locationIsNotEmpty,
  isPrintableCheckPayment,
  checkIfDateIsLocked,
  generateLockTooltip,
} from 'utils/helpers'
import { trackEvent } from 'utils/tracking'
import { request } from 'customRequest'
import CombineTicketsAlert from 'components/containers/bills/components/CombineTicketsAlert'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import useGetSignatureAndFingerprint from 'utils/custom-hooks/useGetSignatureAndFingerprint'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { getRoundingDelta } from 'utils/rounding'
import useRoundingDelta from '../../../../hooks/useRoundingDelta'
import usePaymentAuthorization from 'components/containers/bills/components/usePaymentAuthorization'
import { DEFAULT_PDF_WIDTH } from 'utils/constants'
import useQzTray from 'utils/custom-hooks/useQzTray'
import { useFilteredPaymentMethods } from 'utils/custom-hooks/useFilteredPaymentMethods'

const getNewPaymentMethod = (method = 'cash') => {
  const rowId = randomId()

  return {
    id: rowId,
    name: rowId,
    method,
    idempotencyKey: uuidv4(),
  }
}

const InboundTicketBillContainer = () => {
  const { id: inboundTicketId } = useParams()
  const context = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const { control, getValues, setValue, watch } = useForm(formOptions)

  const [paymentMethods, setPaymentMethods] = useState([])
  const [groupedPayments, setGroupedPayments] = useState(null)
  const multiplePayments = paymentMethods?.length > 1
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [billIsFullyPaid, setBillIsFullyPaid] = useState(false)
  const [openPrintCheckBlade, setOpenPrintCheckBlade] = useState(false)
  const [activeDialog, setActiveDialog] = useState(null)
  const [openPrintCheckPreview, setOpenPrintCheckPreview] = useState(false)
  const [totalNetValue, setTotalNetValue] = useState(0)
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(0)
  const [balanceRemaining, setBalanceRemaining] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  const [billId, setBillId] = useState(null)
  const [customerId, setCustomerId] = useState(null)
  const [paymentsData, setPaymentsData] = useState([])
  const [loadingPaymentsData, setLoadingPaymentsData] = useState(false)
  const [barcode, setBarcode] = useState(null)
  const [printablePaymentMethodText, setPrintablePaymentMethodText] = useState('')
  const [printableCheckNumberText, setPrintableCheckNumberText] = useState('')
  const [openPrintBillDialog, setOpenPrintBillDialog] = useState(false)
  const { dueDateCalculation } = usePaymentTerms({})
  const [openCaptureFingerprintModal, setOpenCaptureFingerprintModal] = useState(false)
  const [openCaptureSignatureModal, setOpenCaptureSignatureModal] = useState(false)
  const [openCapturePhotoModal, setOpenCapturePhotoModal] = useState(false)
  const { qzPrintResponsive, qzPrint } = useQzTray()

  const paymentMethodIsGreensparkPay = useMemo(
    () => paymentMethods.some(pm => pm.method === 'gs_pay'),
    [paymentMethods],
  )

  const [fingerprintImage, setFingerprintImage] = useState({
    filename: '',
    isTemp: true,
  })
  const [signatureImage, setSignatureImage] = useState({
    filename: '',
    isTemp: true,
  })
  const [photoImage, setPhotoImage] = useState({
    filename: '',
    isTemp: true,
  })
  const [paymentDate, setPaymentDate] = useState('')

  const idempotencyKey = useRef([uuidv4()])

  const {
    showSettlementPrintDocument,
    enableMaterialPaymentHolds,
    enablePdfEmails,
    enableFePdfs,
    enableCommodityWeightThresholds,
    enableLockAccountingV2,
  } = useSelector(state => state.featureFlags?.variables || {})

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)

  const [isFetchingPdf, setIsFetchingPdf] = useState(false)

  const checkPaymentsData = useMemo(
    () => paymentsData?.filter(payment => payment?.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase()) || [],
    [paymentsData],
  )
  const unprintedCheckPaymentsData = useMemo(() => paymentsData?.filter(isPrintableCheckPayment) || [], [paymentsData])
  const validationSchema = {}
  unprintedCheckPaymentsData.forEach(payment => {
    validationSchema[payment.id] = Yup.object().shape({
      details: Yup.object().shape({
        payFrom: Yup.string().required('Pay From is Required'),
        checkNumber: Yup.string().required('Check Number is Required'),
      }),
    })
  })

  const {
    handleSubmit: checkPrintHandleSubmit,
    control: checkPrintControl,
    reset: checkPrintReset,
    getValues: checkPrintGetValues,
    setValue: checkPrintSetValue,
    watch: checkPrintWatch,
    trigger: checkPrintTrigger,
    formState: { errors: checkPrintErrors, isValid: checkPrintIsValid },
  } = useForm({ resolver: yupResolver(Yup.object().shape(validationSchema)) })

  const { settings, hasAtm, details, selectedCashDrawerId, cashDrawers } = useSelector(state => state.settings)

  const filteredCashDrawers = useMemo(() => {
    return cashDrawers?.filter(drawer => drawer?.type !== ACCOUNT_PAYMENT_TYPE_ENUMS.ATM)
  }, [cashDrawers])
  const {
    checkSignatureImage,
    mreEntityId,
    paymentThreshold: { hasThreshold: hasPaymentThreshold, ticketValueThreshold, allowedPaymentTypes },
    commodityType: {
      ferrousCostThreshold: {
        hasThreshold: hasFerrousCostThreshold,
        ticketValueThreshold: ferrousTicketValueThreshold,
        allowedPaymentTypes: ferrousAllowedPaymentTypes,
      },
      nonFerrousCostThreshold: {
        hasThreshold: hasNonFerrousCostThreshold,
        ticketValueThreshold: nonFerrousTicketValueThreshold,
        allowedPaymentTypes: nonFerrousAllowedPaymentTypes,
      },
    },
    roundCashPayments,
    roundOnlyATMandCash,
    roundingDirection,
  } = settings?.purchaseSettings?.payment || {}

  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const { data: inboundTicketData, isFetching: isFetchingInboundTicketData } = useGetInboundTicketQuery(
    { ticketId: inboundTicketId },
    { skip: !inboundTicketId },
  )

  const { data: inboundTicketNotes, isLoading: isLoadingInboundTicketNotes } = useGetInboundTicketNotesQuery(
    {
      inboundTicketId,
    },
    { skip: !inboundTicketId },
  )

  const consolidatedNotes =
    inboundTicketNotes
      ?.filter(note => !note.isInternal)
      .map(note => note.note)
      .join('\n') ?? ''

  const paymentAuthorizationHook = usePaymentAuthorization(totalPaymentAmount, inboundTicketData?.netValue?.currency)

  const { data: billData, isFetching: isFetchingBillData } = useGetBillQuery({ billId }, { skip: !billId })

  const paymentMethodsData = useMemo(
    () => (billData?.paymentMethods ? groupBy(billData.paymentMethods, pm => pm.paymentMethod) : {}),
    [billData],
  )

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!billData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return new Date(billData.completionDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
  }, [lockAccountingPeriod, billData, enableLockAccountingV2])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!billData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, billData.completionDate)
  }, [lockAccountingPeriod, billData, enableLockAccountingV2])

  const paymentPageIsLocked = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!inboundTicketData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    const inboundTicketIsLocked =
      new Date(inboundTicketData.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
    return (
      inboundTicketIsLocked &&
      inboundTicketData?.status?.value !== TICKET_STATUSES.PRICE_COMPLETE.id &&
      inboundTicketData?.status?.value !== TICKET_STATUSES.APPROVED.id
    )
  }, [lockAccountingPeriod, inboundTicketData])

  const yardPreferredPaymentMethod = settings?.purchaseSettings?.payment?.yardPreferredPaymentMethod
  const isAutoSaveAndCloseAfterPrintingEnabled =
    settings?.purchaseSettings?.inboundTickets?.autoSaveAndCloseAfterPrintingReceipt

  const { data: customerData, isFetching: isFetchingCustomerData } = useGetCustomerQuery(
    { customerId },
    { skip: !customerId },
  )

  const { data: carrierData, isFetching: isFetchingCarrierData } = useGetCustomerQuery(
    { customerId: inboundTicketData?.carrierId },
    { skip: !inboundTicketData?.carrierId },
  )

  const { data: checkSignatureImageSrc, isFetching: isFetchingCheckSignatureImage } = useGetOrgImageQuery(
    { filename: checkSignatureImage, isTemp: false, expiresIn: 3600 },
    { skip: !checkSignatureImage, refetchOnMountOrArgChange: true },
  )

  const { data: ticketCreator, isFetching: isFetchingTicketCreatorData } = useGetGsUserQuery(
    { userId: inboundTicketData?.createdById },
    { skip: !inboundTicketData },
  )

  const { data: vehicleData, isFetching: isFetchingVehicleData } = useGetVehicleQuery(
    { vehicleId: inboundTicketData?.vehicleId },
    { skip: !inboundTicketData?.vehicleId },
  )

  const { data: queuedPaymentsData, isFetching: isFetchingQueuedPaymentsData } = useGetInboundTicketQueuePaymentsQuery(
    { inboundTicketId: inboundTicketId },
    { skip: !inboundTicketId },
  )

  const { data: purchaseOrderData, isLoading: isLoadingPurchaseOrderData } = useGetPurchaseOrderQuery(
    { purchaseOrderId: inboundTicketData?.transportationInfo?.purchaseOrderId },
    { skip: !inboundTicketData?.transportationInfo?.purchaseOrderId },
  )

  const { materialTypeIds, commodityIds, commodityWeights } = useMemo(() => {
    const materialTypeIdSet = new Set()
    const commodityIdSet = new Set()
    const commodityWeights = {}

    inboundTicketData?.materials?.forEach(material => {
      const materialTypeId = material.materialTypeId || ''
      const commodityId = material.materialType?.commodityId || ''

      materialTypeIdSet.add(materialTypeId)
      commodityIdSet.add(commodityId)

      if (!commodityWeights[commodityId]) commodityWeights[commodityId] = 0
      commodityWeights[commodityId] += material?.weightAndPrice?.netWeight?.amount
    })

    return {
      materialTypeIds: Array.from(materialTypeIdSet),
      commodityIds: Array.from(commodityIdSet),
      commodityWeights,
    }
  }, [inboundTicketData])

  const { data: materialTypesData, isFetching: isFetchingMaterialTypesData } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ materialTypeIds }) },
    { skip: !materialTypeIds || materialTypeIds.length === 0 },
  )

  const { data: commoditiesData, isLoading: isLoadingCommoditiesData } = useGetCommoditiesQuery(
    { pageSize: 1000 }, // once commodityId filtering is implemented, we can switch this to: { filter: JSON.stringify({ commodityIds }) }
    { skip: !commodityIds || commodityIds.length === 0 },
  )

  const [createPayment, { isLoading: isCreatingPayment }] = useCreatePaymentMutation()
  const [updatePayment, { isLoading: isUpdatingPayment }] = useUpdatePaymentMutation()
  const [voidPayment, { isLoading: isVoidingPayment }] = useVoidPaymentMutation()
  const [updateInboundTicket, { isLoading: isLoadingUpdateInboundTicket }] = useUpdateInboundTicketMutation()

  const PAYMENT_METHOD_OPTIONS = useFilteredPaymentMethods({
    hasAtm,
    customerGsPayContactId: customerData?.gsPayContactId,
    paymentMethodsData,
    paymentMethods,
  })

  const isLoading =
    isFetchingInboundTicketData ||
    isLoadingInboundTicketNotes ||
    isFetchingBillData ||
    isFetchingCustomerData ||
    isFetchingCarrierData ||
    isCreatingPayment ||
    isUpdatingPayment ||
    isFetchingCheckSignatureImage ||
    isVoidingPayment ||
    isLoadingUpdateInboundTicket ||
    loadingPaymentsData ||
    isFetchingVehicleData ||
    isFetchingTicketCreatorData ||
    isFetchingQueuedPaymentsData ||
    isFetchingPdf ||
    isFetchingMaterialTypesData ||
    isLoadingCommoditiesData ||
    isLoadingPurchaseOrderData

  useGlobalLoader(isLoading)

  const billTaxRate = useMemo(
    () =>
      parseFloat(
        !billData || billData?.status?.toUpperCase() === BILL_STATUSES.DUE.id
          ? customerData?.taxRate
          : billData?.taxRate,
      ) || 0,
    [billData, customerData],
  )

  const { pricePrepend } = useGetPricePrepend({
    customerData,
    selectedCustomer: { id: customerData?.id },
  })

  const defaultPriceEntries = useMemo(
    () => ({
      Items: [
        {
          entriesObj: materialTypesData?.Items?.map(materialType => ({
            ...(materialType?.defaultPriceEntry || {}),
            commodityId: materialType.commodityId,
            commodityName: materialType.commodityName,
            materialCode: materialType.code,
            materialName: materialType.commonName,
            tareWeightRequired: materialType.tareWeightRequired,
          })),
        },
      ],
    }),
    [materialTypesData],
  )

  const { showWeightThresholdAlert, weightThresholdErrors } = useMemo(() => {
    if (!commoditiesData) return false
    const commoditiesThresholdDict = {}
    commoditiesData.Items?.forEach(commodity => {
      commoditiesThresholdDict[commodity.id] = {
        rule: commodity.rules?.find(rule => rule.type === COMMODITY_RULE_TYPES.WEIGHT_THRESHOLD),
        name: commodity.name,
      }
    })
    const weightThresholdErrors = []

    Object.entries(commodityWeights).forEach(([commodityId, weight]) => {
      const rule = commoditiesThresholdDict[commodityId]?.rule

      if (!rule || !rule.enabled || rule.parameters?.amount >= weight) return

      weightThresholdErrors.push({
        commodityId,
        commodityName: commoditiesThresholdDict[commodityId]?.name,
        threshold: rule.parameters?.amount,
        weight,
      })
    })
    return {
      showWeightThresholdAlert: weightThresholdErrors.length > 0,
      weightThresholdErrors,
    }
  }, [commodityWeights, commoditiesData])

  const roundingDelta = useRoundingDelta(paymentMethods, paymentMethodsData, totalPaymentAmount)
  const { feesAndPromotionsValue, activeFeesAndPromotions } = useMemo(() => {
    if (!inboundTicketData) {
      return {
        feesAndPromotionsValue: 0,
        activeFeesAndPromotions: [],
      }
    }
    const activePaymentMethods = {}
    paymentMethods.forEach(paymentMethod => {
      if (!activePaymentMethods[paymentMethod.method]) {
        activePaymentMethods[paymentMethod.method] = true
      }
    })
    let feesAndPromotionsValue = 0
    const activeFeesAndPromotions = inboundTicketData.feeOrPromotion.filter(feeOrPromotion => {
      if (inboundTicketData?.status?.value === TICKET_STATUSES.PAID.id) {
        if (feeOrPromotion.applied) {
          feesAndPromotionsValue = roundTo(
            feesAndPromotionsValue + feeOrPromotion.calculatedValue,
            ROUNDING_PRECISIONS.COST,
          )
          return true
        }
        return false
      }
      if (feeOrPromotion.eligiblePaymentTypes?.some(paymentType => activePaymentMethods[paymentType])) {
        feesAndPromotionsValue = roundTo(
          feesAndPromotionsValue + feeOrPromotion.calculatedValue,
          ROUNDING_PRECISIONS.COST,
        )
        return true
      }
      return false
    })
    return {
      feesAndPromotionsValue,
      activeFeesAndPromotions,
    }
  }, [inboundTicketData, paymentMethods])

  const fetchPaymentsData = useCallback(async paymentIds => {
    await Promise.all(paymentIds.map(async paymentId => request(`/core-api/purchases/payments/${paymentId}`)))

      .then(results => {
        const filteredResults = results.filter(payment => payment.status !== 'VOID')
        setLoadingPaymentsData(false)
        setPaymentsData(filteredResults)
      })
      .catch(error => {
        setLoadingPaymentsData(false)
        const message = error?.data?.error || error?.message || 'An error occurred loading the payments'
        dispatchSnackbar(dispatch, 'error', message)
      })
  }, [])

  // This will recalculate the total payment amount when the payment methods change
  const recalculateTotalPaymentAmount = useCallback(
    rowId => event => {
      const paymentMethodRows = Object.entries(getValues('payment') || {}).filter(([, payment]) => !payment.disabled)
      const newTotalPaymentAmount = paymentMethodRows.reduce((aggregate, [key, rowData]) => {
        const { amount = 0 } = rowData
        const value = rowId === key ? event?.target?.value || 0 : amount
        return aggregate + Number(value)
      }, 0)
      setTotalPaymentAmount(newTotalPaymentAmount)
    },
    [totalPaymentAmount, paymentMethods],
  )

  useEffect(() => {
    recalculateTotalPaymentAmount()()
  }, [paymentMethods])

  const pdfPrint = (pdfUrl, pdfTitle, documentType = PDF_TYPE.IBT_PURCHASE_TICKET) => {
    setIsFetchingPdf(true)
    request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      .then(async response => {
        // Get appropriate printer based on document type
        const printerKeyMap = {
          [PDF_TYPE.IBT_PURCHASE_TICKET]: 'ticketPrinter',
          [PDF_TYPE.IBT_SCALE_TICKET]: 'ticketPrinter',
          [PDF_TYPE.BILL_SETTLEMENT_DOC]: 'documentPrinter',
          [PDF_TYPE.CHECK]: 'checkPrinter',
        }
        const selectedPrinter = localStorage.getItem(printerKeyMap[documentType])

        if (!selectedPrinter || selectedPrinter === 'none') {
          // Use default printJS if no printer is selected
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: pdfTitle,
          })
        } else {
          // Use appropriate QZ print function based on document type
          try {
            if (documentType === PDF_TYPE.IBT_PURCHASE_TICKET || documentType === PDF_TYPE.IBT_SCALE_TICKET) {
              await qzPrintResponsive(response, selectedPrinter)
            } else if (documentType === PDF_TYPE.CHECK) {
              await qzPrint(response, selectedPrinter, 1) // Pass explicit copies=1
            } else {
              await qzPrint(response, selectedPrinter)
            }
          } catch (error) {
            // Fallback to printJS if QZ fails
            printJS({
              printable: response,
              type: 'pdf',
              base64: true,
              documentTitle: pdfTitle,
            })
          }
        }
        setIsFetchingPdf(false)
      })
      .catch(() => {
        setIsFetchingPdf(false)
        dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
      })
  }

  const printPurchaseTicket = async (id, title) => {
    if (enableFePdfs) {
      if (!id) {
        return
      }

      const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
      pdfPrint(
        `/core-api/purchases/inbound-tickets/${id}/pdf/IBT_PURCHASE_TICKET?width=${paperWidth}`,
        title,
        PDF_TYPE.IBT_PURCHASE_TICKET,
      )
    } else {
      setActiveDialog({
        title: 'Purchase Ticket (Full Page)',
        dialogTitle: title,
        action: PRINT_ACTION.PurchaseFull,
      })
    }
  }

  const onVoidPayment = useCallback(async () => {
    idempotencyKey.current = paymentMethods.map(() => uuidv4())

    setShowVoidModal(false)

    await paymentsData
      .reduce(async (prevPromise, payment) => {
        const results = await prevPromise
        const response = await voidPayment({ paymentId: payment.id })
        return [...results, response]
      }, Promise.resolve([]))
      .then(() => {
        setBarcode(null)
        setBillIsFullyPaid(false)
        recalculateTotalPaymentAmount()()
        // Remove check numbers from payment methods
        Object.entries(getValues('payment') || {}).forEach(([key, payment]) => {
          if (payment.method?.indexOf(PAYMENT_METHODS_VALUES.CHECK.toLowerCase()) === 0) {
            setValue(`payment.${key}.method`, PAYMENT_METHODS_VALUES.CHECK.toLowerCase())
          }
        })
      })
      .catch(error => {
        const errorMsg =
          error?.data?.error || error?.message || `An error occurred voiding the payment${multiplePayments ? 's' : ''}`
        dispatchSnackbar(dispatch, 'error', errorMsg)
      })
  }, [paymentMethods, paymentsData])

  const groupPaymentsByDate = paymentsData => {
    const newPaymentMethods = []
    const groupedPayments = {
      single: [],
      grouped: [],
    }
    const restrictedGroups = {}
    paymentsData.forEach(item => {
      if (item.isRestricted && item.restrictedThroughDate) {
        const date = item.restrictedThroughDate
        if (!restrictedGroups[date]) {
          restrictedGroups[date] = []
        }
        restrictedGroups[date].push(item)
      } else {
        groupedPayments.single.push(item)
        newPaymentMethods.push(item)
      }
    })

    for (const date in restrictedGroups) {
      const group = restrictedGroups[date]
      if (group.length > 1) {
        groupedPayments.grouped.push(group)
      } else {
        groupedPayments.single.push(...group)
      }
      const groupTotal = group.reduce((acc, item) => acc + item.totalAmount, 0)
      newPaymentMethods.push({ ...group[0], totalAmount: groupTotal })
    }
    return { groupedPayments, newPaymentMethods }
  }

  const onSubmitBill = async (shouldPrintAfter = false) => {
    try {
      const netValue = roundTo(
        (billData.netCost || 0) + (roundingDelta || 0) + (feesAndPromotionsValue || 0),
        ROUNDING_PRECISIONS.COST,
      )
      const taxValue = roundTo((netValue * billTaxRate) / 100, ROUNDING_PRECISIONS.COST)
      let paymentMethodsInfo = paymentMethods
      if (enableMaterialPaymentHolds && groupedPayments) {
        const { newPaymentMethods } = groupPaymentsByDate(paymentMethods)
        paymentMethodsInfo = newPaymentMethods
      }

      // if billIsFullyPaid, then we are just updating the check payments
      const paymentsToFormat = billIsFullyPaid
        ? paymentMethodsInfo?.filter(payment => payment.method === PAYMENT_METHODS_VALUES.CHECK.toLowerCase())
        : paymentMethodsInfo

      const formattedPayments = paymentsToFormat?.map((payment, index) => {
        const { id, method, idempotencyKey: _idempotencyKey, totalAmount, isRestricted } = payment
        const paymentAmount =
          enableMaterialPaymentHolds && isRestricted && groupedPayments
            ? totalAmount
            : getValues(`payment.${payment.id}`)?.amount || 0
        const formattedPayment = {
          billId,
          id: billIsFullyPaid ? id : undefined,
          paymentDate: new Date().getTime(),
          completionDate: new Date(dueDateCalculation(new Date(), 'net20')).getTime(),
          paymentMethod:
            method?.indexOf(PAYMENT_METHODS_VALUES.CHECK.toLowerCase()) === 0
              ? PAYMENT_METHODS_VALUES.CHECK.toLowerCase()
              : method,
          accountId: method === 'cash' ? selectedCashDrawerId || filteredCashDrawers?.[0]?.id : undefined,
          paymentNotes: getValues('customer.notes'),
          total: {
            amount: roundTo(paymentAmount * 100),
            currency: inboundTicketData?.netValue?.currency,
            precision: 2,
          },
          purchaseOrderId: inboundTicketData?.transportationInfo?.purchaseOrderId || null,
          status: PAYMENT_STATUSES.PAID.id,
          inboundTickets: [inboundTicketData?.id],
          fingerPrintImageFileName: getValues('fingerPrintImageFileName'),
          signatureImageFileName: getValues('signatureImageFileName'),
          idempotencyKey: _idempotencyKey || idempotencyKey?.current[index],
        }
        if (billIsFullyPaid) {
          const checkingAccountId = checkPrintGetValues(`${id}.details.payFrom`)
          const sanitizedCheckingAccountId =
            checkingAccountId === 'Main Checking Account' ? undefined : checkingAccountId

          formattedPayment.checkNumber = checkPrintGetValues(`${id}.details.checkNumber`)
          formattedPayment.isManualCheckNumber = !checkPrintGetValues(`${id}.details.useAutoincrementedCheckNumber`)
          formattedPayment.accountId = sanitizedCheckingAccountId
          formattedPayment.payToContactId = checkPrintGetValues(`${id}.information.payToTheOrder`)?.id
          formattedPayment.payToLocationId = checkPrintGetValues(`${id}.information.location`)?.id
          formattedPayment.checkMemo = checkPrintGetValues(`${id}.information.memo`)
        }
        return formattedPayment
      })

      if (billIsFullyPaid) {
        const updatePaymentResponses = await formattedPayments.reduce(async (prevPromise, formattedPayment) => {
          const results = await prevPromise
          const response = await updatePayment({
            paymentId: formattedPayment.id,
            payment: formattedPayment,
          })
          return [...results, response]
        }, Promise.resolve([]))
        const updatePaymentError = updatePaymentResponses.find(response => !!response.error)?.error
        if (updatePaymentError) throw new Error(updatePaymentError?.data?.error)

        const responsePaymentData = updatePaymentResponses.map(payment => payment.data)
        setPaymentsData(responsePaymentData)
        setPrintablePaymentMethodText(generatePaymentMethodText(responsePaymentData))
        setPrintableCheckNumberText(generateCheckNumberText(responsePaymentData))
        trackEvent({
          eventName: cz.UPDATE_INBOUND_TICKET,
          description: formatNumber(netValue + taxValue),
        })
      } else {
        const createPaymentResponses = await formattedPayments.reduce(async (prevPromise, formattedPayment) => {
          const results = await prevPromise
          const response = await createPayment({
            billId,
            batchBillId: billData?.batchBillId,
            paymentCreation: formattedPayment,
          })
          return [...results, response]
        }, Promise.resolve([]))
        const createPaymentError = createPaymentResponses.find(response => !!response.error)?.error
        if (createPaymentError && createPaymentError?.data?.code === GS_ERROR_CODE.VALIDATION_ERROR) {
          dispatch(
            showFormModal({
              formModalName: 'payment-restrictions-blade',
              formModalProps: {
                validationErrorPaths: createPaymentError.data.validationErrorPaths,
                customerId: customerId,
              },
            }),
          )
          return
        }
        // if its an error we don't know how to deal with jump to the catch block that launches generic toast
        else if (createPaymentError) {
          throw new Error(createPaymentError?.data?.error)
        }

        const responsePaymentData = createPaymentResponses.map(payment => payment.data)
        setPrintablePaymentMethodText(generatePaymentMethodText(responsePaymentData))
        setPrintableCheckNumberText(generateCheckNumberText(responsePaymentData))
        setPaymentDate(createPaymentResponses[0].data.paymentDate)

        createPaymentResponses.forEach(payment => {
          if (payment.data?.paymentMethod === 'atm' && payment.data?.barcode) {
            setBarcode(payment.data.barcode)
          }
        })
        paymentMethodsInfo.forEach(paymentRow => {
          setValue(`payment.${paymentRow.id}.disabled`, true)
        })
        trackEvent({
          eventName: cz.PAY_INBOUND_TICKET,
          description: formatNumber(netValue + taxValue),
        })
        dispatchSnackbar(dispatch, 'success', `Payment${multiplePayments ? 's' : ''} confirmed`)
        if (formattedPayments.some(isPrintableCheckPayment)) {
          setOpenPrintCheckBlade(true)
        } else if (shouldPrintAfter) {
          printPurchaseTicket(inboundTicketData?.id, `Purchase Ticket #${inboundTicketData?.ticketId} | PAID`)
        }
      }
    } catch (error) {
      const action = billIsFullyPaid ? 'updating' : 'making'
      const errorMsg =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        `An error occurred ${action} the payment${multiplePayments ? 's' : ''}`
      trackEvent({
        eventName: billIsFullyPaid ? cz.UPDATE_INBOUND_TICKET : cz.PAY_INBOUND_TICKET,
        errorMsg,
      })
      dispatchSnackbar(dispatch, 'error', errorMsg)
    }
  }

  const handleCloseActiveDialog = () => setActiveDialog(null)

  const getAddressFromContact = contact => ({
    address1: contact?.address1,
    address2: contact?.address2,
    city: contact?.city,
    state: contact?.state,
    zip: contact?.zip,
  })

  const printableInboundTicketBillData = useMemo(
    () => ({
      ...inboundTicketData,
      date: dateFormatter(inboundTicketData?.effectiveDate),
      customerName: inboundTicketData?.customer?.customerCommonIdentifierString,
      customerId: inboundTicketData?.customer?.id,
      statusValue: TICKET_STATUSES[inboundTicketData?.status?.value]?.name,
      subTotal: billData?.netCost || 0,
      gross: inboundTicketData?.loadWeight?.gross,
      tare: inboundTicketData?.loadWeight?.tare,
      orderNumber: inboundTicketData?.transportationInfo?.purchaseOrderNumber || '-',
      billOfLading: inboundTicketData?.transportationInfo?.billOfLading,
    }),
    [inboundTicketData, billData],
  )

  const validCustomerLocations = useMemo(() => {
    return customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
  }, [customerData])

  const printableInboundTicketData = useMemo(() => {
    let contactLocation = {}
    const primaryContact = customerData?.contacts?.find(contact => !!contact.isPrimaryContact)
    if (primaryContact?.address1) {
      contactLocation = getAddressFromContact(primaryContact)
    } else {
      const firstContact = customerData?.contacts?.find(contact => !!contact.address1)
      if (firstContact) {
        contactLocation = getAddressFromContact(firstContact)
      } else if (validCustomerLocations.length > 0) {
        contactLocation = validCustomerLocations[0]
      }
    }

    const { scrapVehiclesAsMaterials } = formatScrapVehicles(inboundTicketData?.scrapVehicles)

    const formattedMaterials = (inboundTicketData?.materials || []).map(material => {
      const orderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(entry => entry.id === material.orderEntryId)
      return {
        ...material,
        overrideName: orderEntry?.overrideName,
      }
    })

    return {
      ...inboundTicketData,
      netValue: billData?.netCost || 0,
      roundingDelta: roundingDelta || 0,
      taxRate: billTaxRate,
      materials: [...formattedMaterials, ...scrapVehiclesAsMaterials],
      customer: {
        ...inboundTicketData?.customer,
        contactLocation,
      },
      carrierName: carrierData?.displayName,
      feeOrPromotion: activeFeesAndPromotions,
      feeOrPromotionTotal: feesAndPromotionsValue,
    }
  }, [
    inboundTicketData,
    billData,
    carrierData,
    customerData,
    validCustomerLocations,
    billTaxRate,
    roundingDelta,
    purchaseOrderData,
    activeFeesAndPromotions,
    feesAndPromotionsValue,
  ])

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)
    const paymentMethodIsCheck =
      Object.values(getValues('payment') || {}).filter(
        payment => !payment.disabled && payment.method === PAYMENT_METHODS_VALUES.CHECK.toLowerCase(),
      ).length > 0
    const allCheckPaymentsNegative =
      paymentsData.length > 0 &&
      paymentsData
        .filter(payment => payment.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase())
        .every(payment => payment.total < 0)

    const IBT_PAY_ACTION_BUTTONS = {
      CASH_CARD_ACTION: {
        icon: <CreditCardIcon />,
        variant: 'text',
        text: customerData?.contacts?.[0]?.texasCashCardId ? 'VIEW CASH CARD' : 'CREATE CASH CARD',
        id: 'create-cash-card',
        onClick: () => {
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.CREATE_CASH_CARD,
              formModalProps: { customerData },
            }),
          )
        },
        type: ACTION_TYPE.Button,
        disabled: false,
      },
      DIVIDER_ACTION: {
        type: ACTION_TYPE.Divider,
      },
      EMAIL_ACTION: {
        icon: <EmailIcon />,
        variant: 'text',
        text: 'EMAIL',
        id: 'email',
        disabled: isLoading,
        onClick: () => setIsEmailModalOpen(true),
        type: ACTION_TYPE.TooltipButton,
        tooltipProps: {
          title: 'Email Settlement',
        },
      },
      PAY_PRINT_ACTION: {
        icon: <PrintIcon />,
        variant: 'contained',
        text: 'PAY AND PRINT',
        id: 'pay-and-print',
        onClick: () => onSubmitBill(true),
        type: ACTION_TYPE.TooltipButton,
        disabled:
          paymentPageIsLocked ||
          Number(balanceRemaining) !== 0 ||
          !paymentAuthorizationHook.isAuthorized ||
          showWeightThresholdAlert ||
          showCostThresholdAlert,
        tooltipProps: {
          title: paymentPageIsLocked
            ? ENTITY_IN_LOCK_PERIOD_MESSAGE
            : showWeightThresholdAlert
              ? COMMODITY_WEIGHT_THRESHOLD_ALERT_MESSAGE
              : showCostThresholdAlert
                ? COMMODITY_TYPE_COST_THRESHOLD_ALERT_MESSAGE
                : '',
        },
      },
      PAY_THIS_TICKET_ACTION: {
        icon: <AttachMoneyIcon />,
        variant: !paymentMethodIsCheck ? 'outlined' : 'contained',
        text: 'PAY THIS TICKET',
        id: 'inbound-ticket-pay-ticket',
        onClick: () => onSubmitBill(),
        type: ACTION_TYPE.TooltipButton,
        disabled:
          paymentPageIsLocked ||
          Number(balanceRemaining) !== 0 ||
          !paymentAuthorizationHook.isAuthorized ||
          showWeightThresholdAlert ||
          showCostThresholdAlert,
        tooltipProps: {
          title: paymentPageIsLocked
            ? ENTITY_IN_LOCK_PERIOD_MESSAGE
            : showWeightThresholdAlert
              ? COMMODITY_WEIGHT_THRESHOLD_ALERT_MESSAGE
              : showCostThresholdAlert
                ? COMMODITY_TYPE_COST_THRESHOLD_ALERT_MESSAGE
                : '',
        },
      },
      VOID_TICKET_ACTION: {
        text: `VOID PAYMENT${paymentsData.length > 1 ? 'S' : ''}`,
        icon: <FlagIcon />,
        variant: 'text',
        color: 'error',
        id: 'inbound-ticket-void-payment',
        onClick: () => setShowVoidModal(true),
        type: ACTION_TYPE.TooltipButton,
        tooltipProps: {
          title: (() => {
            if (isInLockedAccountingPeriodV1) {
              return ENTITY_IN_LOCK_PERIOD_MESSAGE
            }
            if (paymentsData.some(payment => payment.paymentMethod === 'gs_pay')) {
              return 'Please contact GreenSpark Support to cancel a GreenSpark Pay payment'
            }
            return ''
          })(),
        },
        disabled: isInLockedAccountingPeriodV1 || paymentsData.some(payment => payment.gsPayPayment?.status),
      },
      PRINT_PURCHASE_TICKET_ACTION: {
        text: 'RECEIPT',
        icon: <PrintIcon />,
        variant: 'text',
        id: 'inbound-ticket-purchase-ticket-print',
        disabled: isLoading,
        type: ACTION_TYPE.Button,
        onClick: () =>
          printPurchaseTicket(
            inboundTicketData?.id,
            `Purchase Ticket #${inboundTicketData?.ticketId} | ${
              TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
            }`,
          ),
      },
      PRINT_BILL_SETTLEMENT_ACTION: {
        text: 'Settlement',
        icon: <PrintIcon />,
        disabled: isLoading,
        type: ACTION_TYPE.TooltipButton,
        id: 'bill-print-action-button',
        variant: 'text',
        tooltipProps: {
          title: 'Print settlement',
        },
        onClick: () => {
          if (enableFePdfs) {
            const title = `Payment #${billData?.externalId}`
            pdfPrint(
              `/core-api/purchases/bills/${billData?.id}/pdf/${PDF_TYPE.BILL_SETTLEMENT_DOC}`,
              title,
              PDF_TYPE.BILL_SETTLEMENT_DOC,
            )
          } else {
            setOpenPrintBillDialog(true)
          }
        },
      },
      PRINT_CHECK: {
        text: `PRINT CHECK${unprintedCheckPaymentsData.length > 1 ? 's' : ''}`,
        id: 'inbound-ticket-print-check',
        onClick: () => setOpenPrintCheckBlade(true),
        type: ACTION_TYPE.TooltipButton,
        tooltipProps: {
          title: allCheckPaymentsNegative
            ? 'Checks cannot be printed for negative amounts'
            : unprintedCheckPaymentsData.length === 0
              ? 'If you need to re-print a check, please void the payment first and then create a new check payment'
              : '',
        },
        disabled: unprintedCheckPaymentsData.length === 0 || allCheckPaymentsNegative,
      },
    }

    const pageActions = [IBT_PAY_ACTION_BUTTONS.PAY_THIS_TICKET_ACTION]
    const pageActionsForConfirmed = [IBT_PAY_ACTION_BUTTONS.DIVIDER_ACTION, IBT_PAY_ACTION_BUTTONS.VOID_TICKET_ACTION]
    // only show "pay print" button if payment method is not check
    if (!paymentMethodIsCheck) {
      pageActions.push(IBT_PAY_ACTION_BUTTONS.PAY_PRINT_ACTION)
    }

    // Feature Flags for Settlement and Receipt emails
    if (showSettlementPrintDocument && enablePdfEmails) {
      // Case 1: Both flags are true
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_BILL_SETTLEMENT_ACTION)
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_PURCHASE_TICKET_ACTION)
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.EMAIL_ACTION)
    } else if (showSettlementPrintDocument && !enablePdfEmails) {
      // Case 2: Only showSettlementPrintDocument is true
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_BILL_SETTLEMENT_ACTION)
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_PURCHASE_TICKET_ACTION)
    } else {
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_PURCHASE_TICKET_ACTION)
    }

    if (mreEntityId) {
      pageActions.unshift(IBT_PAY_ACTION_BUTTONS.CASH_CARD_ACTION)
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.CASH_CARD_ACTION)
    }

    if (checkPaymentsData.length > 0) {
      pageActionsForConfirmed.unshift(IBT_PAY_ACTION_BUTTONS.PRINT_CHECK)
    }

    if (inboundTicketData) {
      const ticketStatusId = inboundTicketData?.status?.value?.toUpperCase()?.replaceAll(' ', '_')
      const containerTitle = `Ticket #${inboundTicketData?.ticketId}`
      const chipLabel = TICKET_STATUSES[ticketStatusId]?.name || 'Incomplete'

      const chipIcon = iconMapper[ticketStatusId] || iconMapper.INCOMPLETE

      context.setContainerTitle(
        <Stack direction="row" alignItems="center" spacing={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('bill')} placement="top">
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5">{containerTitle}</Typography>
          <Chip
            icon={billIsFullyPaid ? iconMapper.PAID : chipIcon}
            label={billIsFullyPaid ? 'Paid' : chipLabel}
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              padding: 1,
            }}
          />
        </Stack>,
      )
    } else {
      context.setContainerTitle('Ticket #')
    }

    context.setContainerActions(billIsFullyPaid ? pageActionsForConfirmed : pageActions)
  }, [
    inboundTicketData,
    customerData,
    showSettlementPrintDocument,
    billIsFullyPaid,
    isLoading,
    paymentsData,
    checkPaymentsData,
    balanceRemaining,
    paymentMethods,
    selectedCashDrawerId,
    isInLockedAccountingPeriodV1,
    paymentPageIsLocked,
    paymentAuthorizationHook.isAuthorized,
    showWeightThresholdAlert,
  ])

  useEffect(() => {
    if (inboundTicketData) {
      setValue('customer.passengers', inboundTicketData?.numberOfPassengers)
      setBillId(inboundTicketData?.bill?.id)
      setCustomerId(inboundTicketData?.customer?.id)

      if (inboundTicketData?.contactImage) {
        setPhotoImage({
          filename: inboundTicketData.contactImage,
          isTemp: inboundTicketData.contactImageIsTemp,
        })
      }
    }
  }, [inboundTicketData])

  useEffect(() => {
    setBalanceRemaining(
      setDecimalPrecision(Number(totalNetValue) - Number(totalPaymentAmount), {
        shouldFormatNumber: false,
        decimalPrecisionOverride: 2,
      }),
    )
  }, [totalNetValue, totalPaymentAmount])

  useEffect(() => {
    if (billData) {
      if (billData.isMultiTicket) {
        navigate(`/app/purchases/bills/${billData.id}`)
      }
      const { status } = billData
      setBillIsFullyPaid(status?.toUpperCase() === BILL_STATUSES.PAID.id)
      if (billData?.paymentIds?.length > 0) {
        setLoadingPaymentsData(true)
        fetchPaymentsData(billData.paymentIds)
      } else {
        setPaymentsData([])
      }
    }
  }, [billData])

  // Here tax is calculated and added to the totalNetValue
  useEffect(() => {
    if (billData) {
      const netValue = roundTo(
        (billData.netCost || 0) + (roundingDelta || 0) + (feesAndPromotionsValue || 0),
        ROUNDING_PRECISIONS.COST,
      )
      const taxValue = roundTo((netValue * billTaxRate) / 100, ROUNDING_PRECISIONS.COST)

      setTotalNetValue(
        setDecimalPrecision(netValue + taxValue, {
          shouldFormatNumber: false,
          decimalPrecisionOverride: 2,
        }),
      )
    }
  }, [billData, billTaxRate, roundingDelta, feesAndPromotionsValue])

  const { signatureImageData, fingerprintImageData, imagesAreReady } = useGetSignatureAndFingerprint({
    signatureImageFileName: signatureImage?.filename,
    fingerprintImageFileName: fingerprintImage?.filename,
  })

  useEffect(() => {
    paymentsData.forEach(paymentData => {
      if (paymentData?.fingerPrintImageFileName) {
        setFingerprintImage({
          filename: paymentData.fingerPrintImageFileName,
          isTemp: false,
        })
      }
      if (paymentData?.signatureImageFileName) {
        setSignatureImage({
          filename: paymentData.signatureImageFileName,
          isTemp: false,
        })
      }
      if (paymentData?.paymentDate) {
        setPaymentDate(paymentData.paymentDate)
      }
      if (paymentData?.paymentMethod === 'atm' && paymentData?.barcode) {
        setBarcode(paymentData.barcode)
      }
    })
  }, [paymentsData])

  useEffect(() => {
    if (customerData) {
      setValue('customer.supplierType', customerData?.supplierType || '---')
      setValue('customer.email', customerData?.contacts?.[0]?.email || '')
      setValue('customer.notes', customerData?.notes || '')
      setValue('customer.name', customerData?.customerCommonIdentifierString)

      if (customerData.allowFingerprintReuse && customerData.contacts?.[0]?.thumbprintImage) {
        // When this flag is set and the customer has a stored fingerprint, we should reuse it
        setFingerprintImage({
          filename: customerData.contacts[0].thumbprintImage,
          isTemp: false,
        })

        // Set the value in the form, as it's the one used when POSTing the payment
        setValue('fingerPrintImageFileName', customerData.contacts[0].thumbprintImage)
      }
    }
  }, [customerData])

  useEffect(() => {
    if (
      paymentMethods.length > 0 &&
      enableMaterialPaymentHolds &&
      customerData?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
      paymentMethods.some(p => !!p.isRestricted)
    ) {
      const { groupedPayments } = groupPaymentsByDate(paymentMethods)
      setGroupedPayments(groupedPayments)
    }
  }, [paymentMethods, customerData?.supplierType, enableMaterialPaymentHolds])

  const buildPaymentMethodsFromQueuedPayments = () => {
    const paymentMethod = customerData?.preferredPaymentMethod?.toLowerCase() || yardPreferredPaymentMethod
    const newPaymentMethods = [
      {
        ...getNewPaymentMethod(paymentMethod),
        oldTotalAmount: 0,
        totalAmount: 0,
        isRestricted: false,
      },
    ]
    let roundPayment = false

    if (roundOnlyATMandCash) {
      if (
        paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase() ||
        paymentMethod === PAYMENT_METHODS_VALUES.EFT.toLowerCase()
      ) {
        roundPayment = false
      } else {
        roundPayment = true
      }
    } else {
      roundPayment = true
    }

    queuedPaymentsData.forEach(payment => {
      const { isRestricted, totalAmount } = payment
      const roundingDeltaPayment =
        roundPayment && totalAmount
          ? Number(getRoundingDelta(Big(totalAmount), roundCashPayments, roundingDirection))
          : 0
      if (isRestricted) {
        newPaymentMethods.push({
          ...getNewPaymentMethod(paymentMethod),
          ...payment,
          oldTotalAmount: totalAmount,
          totalAmount: Number(setDecimalPrecision(totalAmount + roundingDeltaPayment, { shouldFormatNumber: false })),
        })
      } else {
        newPaymentMethods[0] = {
          ...newPaymentMethods[0],
          isRestricted,
          oldTotalAmount: Number(
            setDecimalPrecision(newPaymentMethods[0].totalAmount + totalAmount, { shouldFormatNumber: false }),
          ),
          totalAmount: Number(
            setDecimalPrecision(newPaymentMethods[0].totalAmount + totalAmount + roundingDeltaPayment, {
              shouldFormatNumber: false,
            }),
          ),
        }
      }
    })
    return newPaymentMethods
  }

  const onUpdatePaymentMethod = updatedPaymentMethods => {
    if (
      enableMaterialPaymentHolds &&
      customerData?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
      queuedPaymentsData.some(p => !!p.isRestricted)
    ) {
      let round = false
      let applyRoundToCashOrAtm = false
      const allPaymentsAreCashOrAtm = updatedPaymentMethods.every(
        p =>
          p.method === PAYMENT_METHODS_VALUES.CASH?.toLowerCase() ||
          p.method === PAYMENT_METHODS_VALUES.ATM?.toLowerCase(),
      )

      if (roundOnlyATMandCash) {
        if (allPaymentsAreCashOrAtm) {
          round = true
        } else {
          round = false
          applyRoundToCashOrAtm = true
        }
      } else {
        round = true
      }

      const newPaymentMethods = updatedPaymentMethods.map(payment => {
        const { oldTotalAmount, method } = payment
        let roundingDeltaPayment = 0

        if (oldTotalAmount) {
          if (round) {
            roundingDeltaPayment = Number(getRoundingDelta(Big(oldTotalAmount), roundCashPayments, roundingDirection))
          } else if (
            applyRoundToCashOrAtm &&
            (method === PAYMENT_METHODS_VALUES.CASH?.toLowerCase() ||
              method === PAYMENT_METHODS_VALUES.ATM?.toLowerCase())
          ) {
            roundingDeltaPayment = Number(getRoundingDelta(Big(oldTotalAmount), roundCashPayments, roundingDirection))
          }
        }

        const newAmount = Number(
          setDecimalPrecision((oldTotalAmount || 0) + roundingDeltaPayment, { shouldFormatNumber: false }),
        )
        const fieldValue = getValues(`payment.${payment.id}.amount`) || 0
        const fieldValueNumber = Number(fieldValue)
        setValue(`payment.${payment.id}.amount`, oldTotalAmount ? newAmount : fieldValue)
        return { ...payment, totalAmount: oldTotalAmount ? newAmount : fieldValueNumber }
      })
      setPaymentMethods(newPaymentMethods)
    }
  }

  const filterPaymentMethods = paymentMethods =>
    paymentMethods.filter(payment => {
      const { totalAmount, isRestricted } = payment
      if (isRestricted) return true
      return totalAmount > 0
    })

  useEffect(() => {
    if (!billData || !customerData || (enableMaterialPaymentHolds && !queuedPaymentsData)) return

    if (billData.status?.toUpperCase() === BILL_STATUSES.PAID.id) {
      if (paymentsData.length === 0) return

      const newPaymentMethods = paymentsData.map(payment => {
        const { paymentMethod, total, checkNumber, gsPayPayment } = payment
        setValue(`payment.${payment.id}.amount`, total)
        setValue(`payment.${payment.id}.method`, `${paymentMethod}${checkNumber ? ` #${checkNumber}` : ''}`)
        return {
          id: payment.id,
          name: payment.id,
          method: payment.paymentMethod,
          gsPayStatus: gsPayPayment?.status,
        }
      })
      setPaymentMethods(newPaymentMethods)
      setPrintablePaymentMethodText(generatePaymentMethodText(paymentsData))
      setPrintableCheckNumberText(generateCheckNumberText(paymentsData))
    } else if (Object.keys(getValues('payment') || {}).length === 0) {
      const paymentMethod = customerData?.preferredPaymentMethod?.toLowerCase() || yardPreferredPaymentMethod
      if (
        enableMaterialPaymentHolds &&
        customerData?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
        queuedPaymentsData.some(p => !!p.isRestricted)
      ) {
        // build payment methods from queued payments
        const builtPaymentMethods = buildPaymentMethodsFromQueuedPayments()
        const newPaymentMethods = billTaxRate <= 0 ? filterPaymentMethods(builtPaymentMethods) : builtPaymentMethods
        setPaymentMethods(newPaymentMethods)
        newPaymentMethods.forEach(payment => {
          setValue(`payment.${payment.id}.amount`, payment.totalAmount)
        })
      } else {
        setPaymentMethods([getNewPaymentMethod(paymentMethod)])
      }
    }
  }, [billData, paymentsData, customerData, queuedPaymentsData, enableMaterialPaymentHolds])

  const onClickAddPaymentMethod = () => {
    idempotencyKey.current.push(uuidv4())
    const nextPaymentMethod = customerData?.preferredPaymentMethod?.toLowerCase() || yardPreferredPaymentMethod
    const newPaymentMethods = [...paymentMethods, getNewPaymentMethod(nextPaymentMethod)]
    setPaymentMethods(newPaymentMethods)
  }

  const onClickDeletePaymentMethod = paymentMethod => {
    idempotencyKey.current.pop()
    const newPaymentMethods = paymentMethods.filter(pm => pm.id !== paymentMethod.id)
    setPaymentMethods(newPaymentMethods)
    setValue(`payment.${paymentMethod.id}.amount`, 0)
    setValue(`payment.${paymentMethod.id}.disabled`, true)
    recalculateTotalPaymentAmount()()
  }

  const handleSaveCustomerPhoto = async filename => {
    if (filename) {
      setPhotoImage({
        filename,
        isTemp: true,
      })
      const ibtData = formatIbt(inboundTicketData)
      await updateInboundTicket({
        ticketId: inboundTicketId,
        billId: inboundTicketData?.billId,
        inboundTicketSave: {
          ...ibtData,
          contactImage: filename,
          contactImageIsTemp: true,
        },
      })
        .unwrap()
        .then(() => dispatchSnackbar(dispatch, 'success', 'Image Saved'))
        .catch(error => setErrorMessage(error?.data?.error || error?.message || 'An error occurred saving the image.'))
    }
  }

  const showThresholdWarning = useMemo(() => {
    const netValue = roundTo(
      (billData?.netCost || 0) + (roundingDelta || 0) + (feesAndPromotionsValue || 0),
      ROUNDING_PRECISIONS.COST,
    )
    const taxValue = roundTo((netValue * billTaxRate) / 100, ROUNDING_PRECISIONS.COST)
    const totalValue = roundTo(netValue + taxValue, ROUNDING_PRECISIONS.COST)

    return (
      hasPaymentThreshold &&
      totalValue >= Number(ticketValueThreshold) &&
      !['PAID', 'VOID'].includes(inboundTicketData?.status?.value) &&
      customerData?.supplierType === 'Retail' &&
      paymentMethods.map(payment => payment.method).some(method => !allowedPaymentTypes.includes(method))
    )
  }, [
    hasPaymentThreshold,
    inboundTicketData,
    billTaxRate,
    ticketValueThreshold,
    customerData,
    paymentMethods,
    allowedPaymentTypes,
    feesAndPromotionsValue,
  ])

  const { costThresholdErrors, showCostThresholdAlert } = UseCostThresholdAlert({
    inboundTicketData,
    hasFerrousCostThreshold,
    hasNonFerrousCostThreshold,
    ferrousTicketValueThreshold,
    nonFerrousTicketValueThreshold,
    paymentMethods,
    ferrousAllowedPaymentTypes,
    nonFerrousAllowedPaymentTypes,
    commoditiesData,
  })

  const ThresholdTextWarning = useCallback(() => {
    const prefix = `This ticket is over the ${pricePrepend}${ticketValueThreshold} threshold. Remember to pay with `
    const capitalizedPaymentTypes = allowedPaymentTypes.map(type => PAYMENT_METHODS_VALUES[type.toUpperCase()])
    if (capitalizedPaymentTypes.length === 1) {
      return (
        <Typography variant="body2">
          {prefix} <strong>{capitalizedPaymentTypes[0]}</strong>.
        </Typography>
      )
    }

    if (capitalizedPaymentTypes.length === 2) {
      return (
        <Typography variant="body2">
          {prefix} either <strong>{capitalizedPaymentTypes[0]}</strong> or <strong>{capitalizedPaymentTypes[1]}</strong>
          .
        </Typography>
      )
    }

    const itemList = capitalizedPaymentTypes.slice(0, -1).join(', ')
    const lastItem = capitalizedPaymentTypes[capitalizedPaymentTypes.length - 1]
    return (
      <Typography variant="body2">
        {prefix} <strong>{itemList},</strong> or <strong>{lastItem}</strong>.
      </Typography>
    )
  }, [pricePrepend, ticketValueThreshold, allowedPaymentTypes])

  const handleOnAfterPrint = dialogAction => {
    if (dialogAction === PRINT_ACTION.PurchaseFull && isAutoSaveAndCloseAfterPrintingEnabled) {
      navigate('/app/purchases/ticket-library')
    }
  }

  const cashCardExpirationDate = customerData?.contacts?.[0]?.texasCashCardExpirationDate

  const renderGroupedPayments = useCallback(() => {
    if (!groupedPayments) return null
    const commonProps = {
      availableMethods: PAYMENT_METHOD_OPTIONS,
      totalNetValue: parseFloat(totalNetValue),
      control,
      watch,
      setValue,
      paymentConfirmed: billIsFullyPaid,
      paymentMethods,
      setPaymentMethods,
      recalculateTotalPaymentAmount,
      pricePrepend,
      onUpdatePaymentMethod,
      paymentPageIsLocked,
    }

    const totalPayments = (groupedPayments.grouped?.length || 0) + (groupedPayments.single?.length || 0)
    const { newPaymentMethods } = groupPaymentsByDate(paymentMethods)
    const renderMultiplePayments = () => {
      if (groupedPayments.grouped.length === 0) return null
      return groupedPayments.grouped.map(group => {
        const firstPayment = group[0] || {}
        const listOfMaterials = group
          .map(payment => `${payment.materialTypeCommonName} ${pricePrepend}${payment.totalAmount}`)
          .join(', ')
        const groupTotalAmount = group.reduce((acc, payment) => acc + payment.totalAmount, 0)
        return (
          <PaymentMethodRow
            {...commonProps}
            key={firstPayment.id}
            name={`payment.${firstPayment.id}`}
            defaultMethod={firstPayment.method}
            paymentMethod={firstPayment}
            isMultiplePayments
            listOfMaterialsOnText={listOfMaterials}
            multiplePaymentsTotalAmount={groupTotalAmount}
            groupedPayments={groupedPayments}
            setGroupedPayments={setGroupedPayments}
          />
        )
      })
    }
    const renderSinglePayments = () => {
      if (groupedPayments.single.length === 0) return null
      if (totalPayments !== newPaymentMethods.length) return null
      const paymentsRestricted = groupedPayments.single.filter(payment => payment.isRestricted)
      const paymentsNotRestricted = groupedPayments.single.filter(payment => !payment.isRestricted)

      return (
        <>
          {paymentsRestricted.map(payment => (
            <PaymentMethodRow
              {...commonProps}
              key={payment.id}
              name={`payment.${payment.id}`}
              defaultMethod={payment.method}
              onClickDelete={() => onClickDeletePaymentMethod(payment)}
              paymentMethod={payment}
            />
          ))}
          <Divider style={{ marginBottom: '8px' }} />
          {paymentsNotRestricted.map(payment => (
            <PaymentMethodRow
              {...commonProps}
              key={payment.id}
              name={`payment.${payment.id}`}
              defaultMethod={payment.method}
              onClickDelete={() => onClickDeletePaymentMethod(payment)}
              paymentMethod={payment}
            />
          ))}
        </>
      )
    }
    return (
      <>
        {renderMultiplePayments()}
        {renderSinglePayments()}
      </>
    )
  }, [groupedPayments, paymentMethods, totalNetValue, billIsFullyPaid, paymentPageIsLocked])

  const selectedTicketsDataArray = useMemo(() => {
    return inboundTicketData ? [[inboundTicketData]] : []
  }, [inboundTicketData])

  const customersDataArray = useMemo(() => {
    return customerData ? [customerData] : []
  }, [customerData])

  return (
    <>
      <AlertModal
        open={showVoidModal}
        modalType="delete"
        title={`Confirm Void Payment${paymentsData.length > 1 ? 's' : ''}`}
        question={`Are you sure you want to void ${paymentsData.length > 1 ? 'these payments' : 'this payment'}?`}
        description={`All payments for ticket #${inboundTicketData?.ticketId} will be voided.`}
        confirmLabel={`Confirm Void Payment${paymentsData.length > 1 ? 's' : ''}`}
        cancelLabel="CANCEL"
        onClose={() => setShowVoidModal(false)}
        onSubmit={() => onVoidPayment()}
      />
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
          <Box width={{ xs: '100%', md: '40%' }}>
            {inboundTicketData &&
            inboundTicketData.status?.value !== 'PAID' &&
            !paymentPageIsLocked &&
            !showWeightThresholdAlert ? (
              <CombineTicketsAlert customerId={customerData?.id} />
            ) : null}
            {enableCommodityWeightThresholds && showWeightThresholdAlert && (
              <CommodityWeightThresholdAlert weightThresholdErrors={weightThresholdErrors} />
            )}
            {showCostThresholdAlert && <CommodityTypeCostThresholdAlert costThresholdErrors={costThresholdErrors} />}
            {!paymentAuthorizationHook.isAuthorized && !paymentPageIsLocked && (
              <HighBalancePaymentAlert
                thresholdAmount={paymentAuthorizationHook.ticketValueThreshold}
                thresholdAmountFc={paymentAuthorizationHook.convertedThreshold}
                paymentCurrency={inboundTicketData?.netValue?.currency}
              />
            )}
            {isDateInPast(cashCardExpirationDate) && (
              <Alert
                severity="error"
                sx={{
                  marginBottom: 1,
                }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      dispatch(
                        showFormModal({
                          formModalName: FORM_MODALS.CREATE_CASH_CARD,
                          formModalProps: { customerData },
                        }),
                      )
                    }}
                  >
                    View Cash Card
                  </Button>
                }
              >
                {`Cash Card Expired on: ${dateFormatter(cashCardExpirationDate)}`}
              </Alert>
            )}
            <Typography variant="h6" gutterBottom>
              Payment Methods
            </Typography>
            {showThresholdWarning ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  background: theme.palette.warning.light,
                  alignItems: 'center',
                  borderRadius: 1,
                  padding: 2,
                }}
              >
                <WarningAmberSharpIcon />
                <ThresholdTextWarning />
              </Stack>
            ) : (
              <Divider flexItem />
            )}
            {totalPaymentAmount < 0 && inboundTicketData.status?.value !== TICKET_STATUSES.PAID.id && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  background: theme.palette.warning.light,
                  alignItems: 'center',
                  borderRadius: 1,
                  padding: 2,
                }}
              >
                <WarningAmberSharpIcon />
                <Typography variant="body2">You are about to pay a bill with a negative balance</Typography>
              </Stack>
            )}
            <Grid mt={3}>
              {loadingPaymentsData || paymentMethods.length === 0 ? (
                <Loading localized localizedSx={{ height: '100px' }} />
              ) : (
                <>
                  {groupedPayments
                    ? renderGroupedPayments()
                    : paymentMethods
                        .sort((a, b) => b.isRestricted - a.isRestricted)
                        .map(paymentMethod => (
                          <PaymentMethodRow
                            key={paymentMethod.id}
                            name={`payment.${paymentMethod.id}`}
                            defaultMethod={paymentMethod.method}
                            availableMethods={PAYMENT_METHOD_OPTIONS}
                            totalNetValue={parseFloat(totalNetValue)}
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            paymentConfirmed={billIsFullyPaid}
                            paymentMethods={paymentMethods}
                            setPaymentMethods={setPaymentMethods}
                            onClickDelete={() => onClickDeletePaymentMethod(paymentMethod)}
                            recalculateTotalPaymentAmount={recalculateTotalPaymentAmount}
                            pricePrepend={pricePrepend}
                            paymentMethod={paymentMethod}
                            paymentPageIsLocked={paymentPageIsLocked}
                          />
                        ))}
                </>
              )}
            </Grid>
            {!billIsFullyPaid && !paymentPageIsLocked && !paymentMethodIsGreensparkPay && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button startIcon={<AddIcon />} onClick={onClickAddPaymentMethod}>
                  ADD NEW PAYMENT TYPE
                </Button>
                <Typography
                  pr={3}
                  sx={{
                    color: Number(isLoading ? 0 : balanceRemaining) !== 0 ? theme.palette.error.main : 'unset',
                  }}
                >
                  {`${pricePrepend}${formatNumber(isLoading ? '0.00' : balanceRemaining)} balance remaining`}
                </Typography>
              </Stack>
            )}
            {paymentMethodIsGreensparkPay &&
              (!billIsFullyPaid ? (
                <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
                  GreenSpark Pay does not currently support adding additional payment methods.
                </Typography>
              ) : paymentMethods.some(paymentMethod => paymentMethod.gsPayStatus === 'INITIATED') ? (
                <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
                  Cancel a GreenSpark Pay payment by contacting GreenSpark Support before 3:00PM EST.
                </Typography>
              ) : null)}
            <Box display="flex" flexDirection="row" justifyContent="space-between" paddingTop={3}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Box display="flex" flexDirection="row" alignSelf="center">
                {customerData?.id && (
                  <Link
                    to={`/app/customers/${customerData?.id}/account`}
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button startIcon={<OpenInNewIcon />}>View</Button>
                  </Link>
                )}
              </Box>
            </Box>
            <Divider flexItem />
            <Stack spacing={2}>
              <Stack direction="row" mt={3} spacing={2}>
                <Input
                  name="customer.name"
                  label="Customer Name"
                  data-test="customer-name"
                  size="small"
                  variant="standard"
                  fullWidth
                  defaultValue={customerData?.customerCommonIdentifierString || ''}
                  control={control}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  sx={{ flex: 6 }}
                />
                <Input
                  name="customer.supplierType"
                  label="Type"
                  data-test="customer-type"
                  variant="standard"
                  defaultValue=""
                  control={control}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      style: { textTransform: 'capitalize' },
                    },
                  }}
                  sx={{ flex: 6 }}
                />
              </Stack>
              <Stack direction="row" mt={3} spacing={2}>
                <div style={{ width: '100%' }}>
                  <Input
                    name="customer.email"
                    label="Email"
                    data-test="customer-email"
                    variant="standard"
                    defaultValue=""
                    control={control}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                    }}
                  />
                </div>
                {customerData?.id && (
                  <div>
                    <IconButton
                      size="large"
                      color="primary"
                      sx={{ flex: 1 }}
                      onClick={() =>
                        dispatch(
                          showFormModal({
                            formModalName: FORM_MODALS.NEW_CUSTOMER,
                            formModalProps: { type: 'contacts', customerId: customerData?.id },
                          }),
                        )
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </div>
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                <Tooltip title={paymentPageIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''} placement="right">
                  <Input
                    name="customer.passengers"
                    label="# of Passengers"
                    data-test="customer-passengers"
                    type="number"
                    pattern="[0-9]*"
                    variant={billIsFullyPaid ? 'standard' : 'outlined'}
                    defaultValue=""
                    fullWidth
                    control={control}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...(billIsFullyPaid && { disableUnderline: true }),
                      inputProps: {
                        readOnly: billIsFullyPaid,
                        min: 0,
                      },
                    }}
                    disabled={paymentPageIsLocked}
                    sx={{ flex: 6 }}
                  />
                </Tooltip>
              </Stack>
              <Tooltip title={paymentPageIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''} placement="right">
                <div>
                  <Input
                    name="customer.notes"
                    label="Customer Notes"
                    data-test="customer-notes"
                    variant={billIsFullyPaid ? 'standard' : 'outlined'}
                    defaultValue=""
                    fullWidth
                    control={control}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: billIsFullyPaid,
                      ...(billIsFullyPaid && { disableUnderline: true }),
                    }}
                    disabled={paymentPageIsLocked}
                  />
                </div>
              </Tooltip>
              <Tooltip title={paymentPageIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''} placement="right">
                <Stack flexDirection="row">
                  <Button
                    sx={{
                      width: '100%',
                      marginRight: 1,
                    }}
                    variant="outlined"
                    startIcon={<FingerprintIcon />}
                    onClick={() => setOpenCaptureFingerprintModal(true)}
                    disabled={paymentPageIsLocked}
                  >
                    {fingerprintImage.filename ? 'View Fingerprint' : 'Capture Fingerprint'}
                  </Button>
                  <Button
                    sx={{
                      width: '100%',
                      marginLeft: 1,
                    }}
                    variant="outlined"
                    onClick={() => setOpenCaptureSignatureModal(true)}
                    disabled={paymentPageIsLocked}
                  >
                    {signatureImage.filename ? 'View Signature' : 'Capture Signature'}
                  </Button>
                </Stack>
              </Tooltip>
              <Tooltip title={paymentPageIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''} placement="right">
                <Stack flexDirection="row" width="50%">
                  <Button
                    sx={{
                      width: '100%',
                      marginRight: 1,
                    }}
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => setOpenCapturePhotoModal(true)}
                    disabled={paymentPageIsLocked}
                  >
                    {photoImage.filename ? 'View Photo' : 'Take Photo'}
                  </Button>
                </Stack>
              </Tooltip>
            </Stack>
          </Box>
          <Box width={{ xs: '100%', md: '65%' }} spacing={2} paddingX={2}>
            <ScaleReceiptForm
              inboundTicketData={printableInboundTicketData}
              notes={inboundTicketNotes}
              checkNumber={generateCheckNumberText(checkPaymentsData)}
              paymentConfirmed={billIsFullyPaid}
              pricePrepend={pricePrepend}
              plateNumber={vehicleData?.plateNumber}
            />
          </Box>
        </Stack>
      </Stack>
      <PrintCheckBlade
        open={openPrintCheckBlade}
        setOpen={setOpenPrintCheckBlade}
        checkPaymentsData={unprintedCheckPaymentsData}
        selectedTicketsData={selectedTicketsDataArray}
        customersData={customersDataArray}
        handleSubmit={checkPrintHandleSubmit}
        setValue={checkPrintSetValue}
        watch={checkPrintWatch}
        trigger={checkPrintTrigger}
        control={checkPrintControl}
        reset={checkPrintReset}
        errors={checkPrintErrors}
        isValid={checkPrintIsValid}
        onPrint={async () => {
          if (enableFePdfs) {
            await onSubmitBill()
            setOpenPrintCheckBlade(false)
            pdfPrint(`/core-api/purchases/payments/${billId}/pdf/${PDF_TYPE.CHECK}`, '', PDF_TYPE.CHECK)
            await fetchPaymentsData(billData.paymentIds)
          } else {
            setOpenPrintCheckBlade(false)
            setOpenPrintCheckPreview(true)
          }
        }}
        isLoadingPaymentsData={loadingPaymentsData}
        pricePrepend={pricePrepend}
      />
      {openPrintCheckPreview && (
        <PrintDialog
          title="Print Check Info to Template"
          open={openPrintCheckPreview}
          onClose={() => setOpenPrintCheckPreview(false)}
          dialogId="print-check-dialog"
          shouldPrintTitle={false}
          dialogType={PRINT_DIALOG_TYPES.CHECK}
          onAfterPrint={async () => {
            await onSubmitBill() // this saves the updated check numbers
            setActiveDialog({
              title: 'Purchase Ticket (Full Page)',
              dialogTitle: `Purchase Ticket #${inboundTicketData?.ticketId} | PAID`,
              action: PRINT_ACTION.PurchaseFull,
            })

            await fetchPaymentsData(billData.paymentIds)
          }}
        >
          {unprintedCheckPaymentsData.map((payment, index) => (
            <Box key={payment.id} className={index > 0 ? 'pagebreak' : null} pt={index > 0 ? 3.3 : 0}>
              <CheckTemplate
                key={payment.id}
                checkRecipient={
                  [
                    checkPrintGetValues(`${payment.id}.information.payToTheOrder`).firstName,
                    checkPrintGetValues(`${payment.id}.information.payToTheOrder`).lastName,
                  ]
                    .join(' ')
                    .trim() || inboundTicketData?.customer?.displayName
                }
                customerAddress={
                  checkPrintGetValues(`${payment.id}.information.location`) ||
                  validCustomerLocations[0] ||
                  getContactAddress(customerData)
                }
                textAmount={CurrencyToWords(parseFloat(checkPrintGetValues(`${payment.id}.details.checkAmount`)))}
                paymentDate={dateFormatter(payment.paymentDate)}
                amount={parseFloat(checkPrintGetValues(`${payment.id}.details.checkAmount`)).toFixed(2)}
                memo={checkPrintGetValues(`${payment.id}.information.memo`)}
                checkSignatureImageSrc={checkSignatureImageSrc}
              />
            </Box>
          ))}
        </PrintDialog>
      )}

      {activeDialog && imagesAreReady && (
        <PrintDialog
          title={activeDialog.dialogTitle}
          open={!!activeDialog}
          onClose={() => {
            handleCloseActiveDialog()
            activeDialog?.onClose?.()
          }}
          dialogType={activeDialog.dialogType}
          showLogoAddress
          onAfterPrint={() => handleOnAfterPrint(activeDialog.action)}
          headerOverride={
            <>
              {barcode && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'right',
                    paddingRight: 2,
                  }}
                >
                  <Barcode value={barcode} width={3} />
                </Box>
              )}
            </>
          }
        >
          {renderActiveDialog({
            inboundTicketData: printableInboundTicketData,
            ticketCreator,
            notes: inboundTicketNotes?.map(note => {
              return { ...note, name: note.note }
            }),
            activeDialog,
            paymentData: {
              paymentDate,
              signatureImageData,
              fingerprintImageData,
              paymentMethod: printablePaymentMethodText,
              checkNumber: printableCheckNumberText,
            },
            customerData,
            vehicleData,
            pricePrepend,
          })}
        </PrintDialog>
      )}

      {openPrintBillDialog && (
        <PrintDialog
          title={`Payment #${billData?.externalId}`}
          open={openPrintBillDialog}
          onClose={() => {
            setOpenPrintBillDialog(false)
          }}
          showLogoAddress
        >
          <PrintableBill
            customerData={customerData}
            paymentsData={paymentsData}
            billData={billData}
            billTaxRate={billTaxRate}
            notes={consolidatedNotes}
            tickets={[printableInboundTicketBillData]}
            priceSheet={purchaseOrderData?.priceSheet}
            priceEntriesByPurchaseOrderId={null}
            defaultPriceSheet={defaultPriceEntries}
            summaryData={{
              totalGross: inboundTicketData?.loadWeight?.gross,
              totalTare: inboundTicketData?.loadWeight?.tare,
              totalNet: inboundTicketData?.netWeight,
              totalValue: totalNetValue,
              remainingBalance: balanceRemaining,
            }}
            pricePrepend={pricePrepend}
          />
        </PrintDialog>
      )}

      {openCaptureFingerprintModal && (
        <CaptureFingerprintModal
          title="Capture Fingerprint"
          hideDividers
          open={openCaptureFingerprintModal}
          onSave={filename => {
            setValue('fingerPrintImageFileName', filename)
            setFingerprintImage({
              filename,
              isTemp: true,
            })
          }}
          onClose={() => setOpenCaptureFingerprintModal(false)}
          fingerprintImage={fingerprintImage}
          dialogStyles={{
            '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
          }}
        />
      )}
      {openCaptureSignatureModal && (
        <CaptureSignatureModal
          title="Capture Signature"
          hideDividers
          open={openCaptureSignatureModal}
          onSave={filename => {
            setValue('signatureImageFileName', filename)
            setSignatureImage({
              filename,
              isTemp: true,
            })
          }}
          onClose={() => setOpenCaptureSignatureModal(false)}
          signatureImage={signatureImage}
          dialogStyles={{
            '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
          }}
        />
      )}
      {openCapturePhotoModal && (
        <CapturePhotoModal
          title={photoImage.filename ? 'View Photo' : 'Take Photo'}
          hideDividers
          open={openCapturePhotoModal}
          onSave={filename => handleSaveCustomerPhoto(filename)}
          onClose={() => setOpenCapturePhotoModal(false)}
          photoImage={photoImage}
          dialogStyles={{
            '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
          }}
        />
      )}
      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        recipients={customerData?.contacts?.map(contact => contact.email).filter(Boolean)}
        pdfTypeOptions={[PDF_TYPE.BILL_SETTLEMENT_DOC]}
        defaultPdfType={PDF_TYPE.BILL_SETTLEMENT_DOC}
        entityId={billId}
      />
    </>
  )
}

export default InboundTicketBillContainer
